import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'

/**
 * Custom hook for handling keyboard shortcuts
 *
 * @param {string[]} keys - Array of key values to listen for (e.g., ['Shift', 'Enter', 'Escape'])
 * @param {Function} callback - Function to call when key is pressed. Receives the keyboard event.
 * @param {HTMLElement|null} node - Optional DOM node to attach listeners to. Defaults to document.
 *
 * @example
 * useKeyPress(['Shift'], (event) => {
 *   if (event.type === 'keydown') console.log('Shift pressed')
 *   if (event.type === 'keyup') console.log('Shift released')
 * })
 */
export const useKeyPress = (keys, callback, node = null) => {
  // Implement the callback ref pattern to avoid stale closures
  // This ensures we always call the latest version of the callback
  const callbackRef = useRef(callback)

  useLayoutEffect(() => {
    callbackRef.current = callback
  }, [callback])

  // Handle keyboard events
  const handleKeyPress = useCallback(
    (event) => {
      // Check if the pressed key matches any of the target keys
      // Using event.key for better cross-browser compatibility
      if (keys.some((key) => event.key === key)) {
        callbackRef.current(event)
        return true
      }
      return false
    },
    [keys]
  )

  useEffect(() => {
    // Target is either the provided node or the document
    const targetNode = node ?? document

    if (!targetNode) {
      return
    }

    // Attach event listeners for both keydown and keyup
    targetNode.addEventListener('keydown', handleKeyPress)
    targetNode.addEventListener('keyup', handleKeyPress)

    // Cleanup: remove event listeners on unmount or when dependencies change
    return () => {
      targetNode.removeEventListener('keydown', handleKeyPress)
      targetNode.removeEventListener('keyup', handleKeyPress)
    }
  }, [handleKeyPress, node])
}

/**
 * Custom hook for handling keyboard shortcuts with modifier keys
 *
 * @param {Object} options - Configuration object
 * @param {string} options.key - Primary key to listen for (e.g., 's', 'Enter')
 * @param {boolean} options.ctrl - Whether Ctrl key must be pressed
 * @param {boolean} options.shift - Whether Shift key must be pressed
 * @param {boolean} options.alt - Whether Alt key must be pressed
 * @param {boolean} options.meta - Whether Meta/Command key must be pressed
 * @param {Function} options.callback - Function to call when shortcut is triggered
 * @param {HTMLElement|null} options.node - Optional DOM node to attach listeners to
 * @param {boolean} options.preventDefault - Whether to prevent default behavior (default: true)
 *
 * @example
 * useKeyboardShortcut({
 *   key: 's',
 *   ctrl: true,
 *   callback: () => console.log('Ctrl+S pressed'),
 *   preventDefault: true
 * })
 */
export const useKeyboardShortcut = ({
  key,
  ctrl = false,
  shift = false,
  alt = false,
  meta = false,
  callback,
  node = null,
  preventDefault = true
}) => {
  const callbackRef = useRef(callback)

  useLayoutEffect(() => {
    callbackRef.current = callback
  }, [callback])

  const handleKeyDown = useCallback(
    (event) => {
      const matchesKey = event.key === key || event.key.toLowerCase() === key.toLowerCase()
      const matchesCtrl = ctrl ? event.ctrlKey : true
      const matchesShift = shift ? event.shiftKey : true
      const matchesAlt = alt ? event.altKey : true
      const matchesMeta = meta ? event.metaKey : true

      if (matchesKey && matchesCtrl && matchesShift && matchesAlt && matchesMeta) {
        if (preventDefault) {
          event.preventDefault()
        }
        callbackRef.current(event)
      }
    },
    [key, ctrl, shift, alt, meta, preventDefault]
  )

  useEffect(() => {
    const targetNode = node ?? document

    if (!targetNode) {
      return
    }

    targetNode.addEventListener('keydown', handleKeyDown)

    return () => {
      targetNode.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown, node])
}
